(function (root) {
  'use strict';
  const GIFT = 68059027734679;
  const QUALIFIERS = [46935420469399, 46935454023831, 46935515594903, 46935537221783, 48195253534871, 48195545792663, 48195695509655];
  const CLAIM = 'as_firstgift_claim';
  const OPTOUT = 'as_firstgift_optout';
  const MARK = '_as_firstgift';
  const managed = (item) => item.variant_id === GIFT && item.properties?.[MARK] === 'v1';
  const qualifies = (cart) => cart.items.some((item) => QUALIFIERS.includes(item.variant_id) && item.quantity > 0 && item.final_line_price > 0);
  const free = (item) => item.final_line_price === 0 && item.line_level_discount_allocations?.some((allocation) => allocation.discount_application?.title?.toUpperCase() === 'FIRSTGIFT');

  function createController(io) {
    let tail = Promise.resolve();
    let lastCart;
    const serial = (work) => {
      const run = tail.catch(() => {}).then(() => io.lock(work));
      tail = run;
      return run;
    };
    const read = async () => (lastCart = await io.read());
    const removeManaged = async (cart) => {
      const updates = Object.fromEntries(cart.items.filter(managed).map((item) => [item.key, 0]));
      if (!Object.keys(updates).length) return cart;
      await io.post('update', { updates });
      return read();
    };
    const reconcile = async (claim = false) => {
      let cart = await read();
      if (claim && cart.attributes?.[CLAIM] !== '1') {
        await io.post('update', { attributes: { [CLAIM]: '1' } });
        cart = await read();
      }
      const active = cart.attributes?.[CLAIM] === '1';
      io.active?.(active);
      const manualGift = cart.items.some((item) => item.variant_id === GIFT && !managed(item));
      if (!active || cart.attributes?.[OPTOUT] === '1' || !qualifies(cart) || manualGift) {
        cart = await removeManaged(cart);
        io.status?.(!active ? 'idle' : cart.attributes?.[OPTOUT] === '1' ? 'removed' : manualGift ? 'manual' : 'choose');
        return cart;
      }
      let gifts = cart.items.filter(managed);
      if (gifts.length > 1 || gifts.some((item) => item.quantity !== 1)) {
        const updates = Object.fromEntries(gifts.map((item, index) => [item.key, index === 0 ? 1 : 0]));
        await io.post('update', { updates });
        cart = await read();
        gifts = cart.items.filter(managed);
      }
      if (!gifts.length) {
        await io.post('add', { items: [{ id: GIFT, quantity: 1, properties: { [MARK]: 'v1' } }] });
        cart = await read();
      }
      const codes = (cart.discount_codes || []).map((item) => item.code).filter(Boolean);
      if (!codes.some((code) => code.toUpperCase() === 'FIRSTGIFT') || !cart.items.filter(managed).every(free)) {
        await io.post('update', { discount: [...new Set([...codes, 'FIRSTGIFT'])].join(',') });
        cart = await read();
      }
      if (!qualifies(cart) || cart.items.filter(managed).some((item) => item.quantity !== 1 || !free(item))) {
        cart = await removeManaged(cart);
        io.status?.('unavailable');
      } else {
        io.status?.('ready');
      }
      return cart;
    };
    const guarded = (work) => serial(async () => {
      io.busy?.(true);
      try { return await work(); }
      catch (error) { io.status?.('error'); throw error; }
      finally { io.busy?.(false); }
    });
    return {
      sync: (claim = false) => guarded(() => reconcile(claim)),
      mutate: (operation, options = {}) => guarded(async () => {
        let cart = await read();
        if (options.removeKey && cart.items.some((item) => managed(item) && item.key === options.removeKey)) {
          await io.post('update', { attributes: { [OPTOUT]: '1' } });
        }
        const result = await operation(cart);
        cart = await reconcile();
        return { result, cart };
      }),
      checkout: () => guarded(async () => {
        const cart = await reconcile();
        if (cart.items.filter(managed).some((item) => !free(item))) throw new Error('Gift has not been verified free');
        return cart;
      }),
      get cart() { return lastCart; },
    };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { createController, GIFT, QUALIFIERS, CLAIM, OPTOUT, MARK };
  if (!root?.document || !root.ASFirstGiftConfig) return;
  const config = root.ASFirstGiftConfig;
  const nativeFetch = root.fetch.bind(root);
  const base = root.Shopify?.routes?.root || '/';
  const messages = config.messages;
  let active = config.active || new URL(root.location.href).searchParams.get('firstgift') === '1';
  let busy = false;
  let checkoutPass = false;
  let ready;
  const renderStatus = (state) => {
    document.documentElement.classList.toggle('as-firstgift-active', active);
    const node = document.getElementById('as-firstgift-status');
    if (node) { node.textContent = active ? messages[state] || messages.choose : ''; node.hidden = !active; }
  };
  const request = async (path, options) => {
    const response = await nativeFetch(base + path, { credentials: 'same-origin', cache: 'no-store', ...options, signal: AbortSignal.timeout(12000) });
    const json = await response.json();
    if (!response.ok || json.errors || json.status >= 400) throw new Error(json.description || 'Cart request failed');
    return json;
  };
  const controller = createController({
    read: () => request('cart.js'),
    post: (action, body) => request('cart/' + action + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
    lock: (work) => {
      if (!navigator.locks) return Promise.reject(new Error('Safe cart coordination unavailable'));
      return navigator.locks.request('as-firstgift-cart', work);
    },
    active: (value) => { active = value; renderStatus('choose'); },
    busy: (value) => { busy = value; document.documentElement.classList.toggle('as-firstgift-busy', value); },
    status: renderStatus,
  });
  const refreshSections = async (ids) => {
    if (!ids?.length) return {};
    const url = new URL(root.location.href);
    url.searchParams.set('sections', ids.join(','));
    const response = await nativeFetch(url, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(12000) });
    if (!response.ok) throw new Error('Cart display refresh failed');
    return response.json();
  };
  root.ASFirstGift = {
    async fetch(url, options, key) {
      try { await ready; }
      catch (_) { if (active) await controller.sync(); }
      const isForm = options.body instanceof FormData;
      const body = isForm ? options.body : JSON.parse(options.body || '{}');
      const sectionValue = isForm ? body.get('sections') : body.sections;
      const sectionIds = typeof sectionValue === 'string' ? sectionValue.split(',') : sectionValue;
      if (!active && !controller.cart?.attributes?.[CLAIM]) return nativeFetch(url, options);
      const { result, cart } = await controller.mutate(async (freshCart) => {
        if (!isForm && body.line) {
          if (!key || !freshCart.items.some((item) => item.key === key)) throw new Error('Cart changed; please refresh');
          delete body.line;
          body.id = key;
          options = { ...options, body: JSON.stringify(body) };
        }
        const response = await nativeFetch(url, options);
        const data = await response.json();
        if (!response.ok || data.errors || data.status >= 400) throw new Error(data.description || 'Cart request failed');
        return data;
      }, { removeKey: !isForm && body.quantity === 0 ? key : null });
      const data = /\/change(?:\.js)?/.test(url) ? cart : result;
      if (/\/add(?:\.js)?/.test(url) && data.key) {
        const properties = (value) => JSON.stringify(Object.entries(value || {}).sort(([a], [b]) => a.localeCompare(b)));
        const current = cart.items.find((item) =>
          item.variant_id === (data.variant_id || data.id) &&
          properties(item.properties) === properties(data.properties) &&
          item.selling_plan_allocation?.selling_plan?.id === data.selling_plan_allocation?.selling_plan?.id
        );
        if (!current) throw new Error('Added item changed; please refresh your cart');
        data.key = current.key;
      }
      data.sections = await refreshSections(sectionIds);
      return new Response(JSON.stringify(data), { headers: { 'Content-Type': 'application/json' } });
    },
  };
  document.addEventListener('submit', async (event) => {
    const submitter = event.submitter;
    if (checkoutPass || !active || submitter?.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (busy) return;
    try {
      await controller.checkout();
      checkoutPass = true;
      event.target.requestSubmit(submitter);
    } catch (_) { renderStatus('error'); }
    finally { checkoutPass = false; }
  }, true);
  document.addEventListener('click', async (event) => {
    const link = event.target.closest('a[href]');
    if (!active || !link || new URL(link.href).pathname !== '/checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (busy) return;
    try { await controller.checkout(); root.location.assign(link.href); }
    catch (_) { renderStatus('error'); }
  }, true);
  renderStatus('choose');
  ready = controller.sync(new URL(root.location.href).searchParams.get('firstgift') === '1');
  const refreshCartUI = (cart) => {
    if (active && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      return publish(PUB_SUB_EVENTS.cartUpdate, { source: 'firstgift-controller', cartData: cart });
    }
  };
  ready.then(refreshCartUI).catch(() => renderStatus('error'));
  root.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      ready = controller.sync();
      ready.then(refreshCartUI).catch(() => renderStatus('error'));
    }
  });
})(typeof window !== 'undefined' ? window : null);
